import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, Flag, EyeOff, Trash2, CheckCircle2, Loader2 } from "lucide-react";
import RatingStars from "@/components/RatingStars";

const TABS = [
  { value: "all", label: "Todas" },
  { value: "published", label: "Publicadas" },
  { value: "pending", label: "Pendientes" },
  { value: "hidden", label: "Ocultas" },
  { value: "removed", label: "Removidas" },
];

export default function StaffReviewsModeracion() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("superadmin_dcc");
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = status === "all" ? "" : `?status=${status}`;
      const r = await api.get(`/admin/reviews${q}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const moderate = async (id, action) => {
    let notes = "";
    if (action === "remove" || action === "hide") {
      notes = prompt(`Nota interna para ${action}:`) || "";
    }
    try {
      await api.put(`/admin/reviews/${id}/moderate`, { action, notes });
      toast.success("Revisión aplicada.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Moderación</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Opiniones de alumnos</h1>
        <p className="mt-1 text-sm text-gray-400">Aprobación editorial, ocultar y remover reviews reportadas.</p>
      </div>

      <div className="flex flex-wrap gap-1 mb-4" data-testid="reviews-mod-tabs">
        {TABS.map((t) => (
          <button key={t.value} onClick={() => setStatus(t.value)}
                  data-testid={`reviews-mod-tab-${t.value}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-widest ${status === t.value ? "bg-[#F26A21] text-[#0B0F14]" : "border border-white/10 text-gray-300 hover:bg-white/5"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400" data-testid="reviews-mod-empty">
          Sin opiniones en este estado.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`reviews-mod-row-${r.id}`}
                 className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-widest text-gray-500">
                    {r.program_title} · {r.moderation_status}
                    {r.flag_count > 0 && <span className="ml-2 text-red-300 inline-flex items-center gap-1"><Flag className="w-3 h-3" /> {r.flag_count}</span>}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <RatingStars value={r.rating} size="sm" showCount={false} />
                    <span className="text-xs text-gray-400">{r.user_name}</span>
                    <span className="text-[10px] text-gray-500">{new Date(r.created_at).toLocaleString("es-MX")}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-white">{r.title}</p>
                  <p className="mt-1 text-sm text-gray-300 whitespace-pre-wrap">{r.body}</p>
                </div>
                <div className="flex flex-wrap gap-1 flex-shrink-0">
                  {r.moderation_status !== "published" && (
                    <button onClick={() => moderate(r.id, "publish")}
                            data-testid={`reviews-mod-publish-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-500 text-[#0B0F14] px-2.5 py-1 text-xs font-semibold hover:brightness-110">
                      <CheckCircle2 className="w-3 h-3" /> Publicar
                    </button>
                  )}
                  {r.moderation_status !== "hidden" && (
                    <button onClick={() => moderate(r.id, "hide")}
                            data-testid={`reviews-mod-hide-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-white/20 text-white px-2.5 py-1 text-xs hover:bg-white/5">
                      <EyeOff className="w-3 h-3" /> Ocultar
                    </button>
                  )}
                  <button onClick={() => moderate(r.id, "remove")}
                          data-testid={`reviews-mod-remove-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-red-500/40 text-red-300 px-2.5 py-1 text-xs hover:bg-red-500/10">
                    <Trash2 className="w-3 h-3" /> Remover
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </StaffLayout>
  );
}
